import { doc, setDoc } from 'firebase/firestore';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { User } from '@h2h/shared';
import { db } from '../../lib/firebase';
import {
  createMiniLeague,
  deleteAccount,
  deleteMiniLeague,
  joinLeague,
  joinMiniLeague,
  kickMiniLeagueMember,
  leaveMiniLeague,
  markNotificationsRead,
  previewMiniLeague,
  renameMiniLeague,
  submitCyclingEntry,
  submitEntry,
  updateMiniLeague,
  type CreateMiniLeagueInput,
  type SubmitCyclingEntryInput,
  type SubmitEntryInput,
  type UpdateMiniLeagueInput
} from '../../lib/api';

/** Submit (or edit) the player's entry for a round (callable). Invalidates the
 *  owner's entry + the public board on success. */
export function useSubmitEntry(uid: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SubmitEntryInput) => submitEntry(input),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['entry', vars.leagueId, vars.roundId, uid] });
      qc.invalidateQueries({ queryKey: ['group', vars.leagueId, vars.roundId, uid] });
    }
  });
}

/** Submit (or edit) a CYCLING stage entry (duel picks). Same cache invalidation as
 *  football's useSubmitEntry — the entry + the public board for the round. */
export function useSubmitCyclingEntry(uid: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SubmitCyclingEntryInput) => submitCyclingEntry(input),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['entry', vars.leagueId, vars.roundId, uid] });
      qc.invalidateQueries({ queryKey: ['group', vars.leagueId, vars.roundId, uid] });
    }
  });
}

/**
 * Merge-write the signed-in user's own profile doc — the single client-writable
 * doc (see config/firestore.rules). Used for preferences (favourite team, default
 * league, notification toggles). Invalidates the cached profile on success.
 */
export function useUpdateUserDoc(uid: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch: Partial<User>) => {
      if (!uid) throw new Error('Not signed in');
      await setDoc(doc(db, 'users', uid), { userId: uid, ...patch }, { merge: true });
    },
    // Optimistically merge the patch into the cached profile so toggles/pickers
    // reflect instantly; roll back if the write fails, then resync from the server.
    onMutate: async (patch) => {
      if (!uid) return { previous: undefined };
      await qc.cancelQueries({ queryKey: ['user', uid] });
      const previous = qc.getQueryData<User | null>(['user', uid]);
      qc.setQueryData<User | null>(['user', uid], (old) => (old ? { ...old, ...patch } : old));
      return { previous };
    },
    onError: (_err, _patch, context) => {
      if (uid && context?.previous !== undefined) {
        qc.setQueryData(['user', uid], context.previous);
      }
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['user', uid] })
  });
}

/** Permanently delete the user's account + competitive data (callable). */
export function useDeleteAccount() {
  return useMutation({ mutationFn: () => deleteAccount() });
}

/** Join a league (callable). Invalidates membership/count queries on success. */
export function useJoinLeague(uid: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (leagueId: string) => joinLeague(leagueId),
    onSuccess: (_data, leagueId) => {
      qc.invalidateQueries({ queryKey: ['memberships', uid] });
      qc.invalidateQueries({ queryKey: ['memberCount', leagueId] });
    }
  });
}
export function useCreateMiniLeague(uid: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateMiniLeagueInput) => createMiniLeague(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['miniLeagues', uid] })
  });
}

export function useJoinMiniLeague(uid: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (inviteCode: string) => joinMiniLeague(inviteCode),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['miniLeagues', uid] })
  });
}

export function useLeaveMiniLeague(uid: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (miniLeagueId: string) => leaveMiniLeague(miniLeagueId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['miniLeagues', uid] })
  });
}

/** Look up a mini league by its invite code (powers the Join "code found" card). */
export function usePreviewMiniLeague() {
  return useMutation({ mutationFn: (inviteCode: string) => previewMiniLeague(inviteCode) });
}

export function useRenameMiniLeague(uid: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { miniLeagueId: string; name: string }) => renameMiniLeague(input.miniLeagueId, input.name),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['miniLeagues', uid] });
      qc.invalidateQueries({ queryKey: ['miniLeague', vars.miniLeagueId] });
    }
  });
}

export function useUpdateMiniLeague(uid: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateMiniLeagueInput) => updateMiniLeague(input),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['miniLeagues', uid] });
      qc.invalidateQueries({ queryKey: ['miniLeague', vars.miniLeagueId] });
    }
  });
}

export function useKickMiniLeagueMember(uid: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { miniLeagueId: string; userId: string }) =>
      kickMiniLeagueMember(input.miniLeagueId, input.userId),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['miniLeagues', uid] });
      qc.invalidateQueries({ queryKey: ['miniLeague', vars.miniLeagueId] });
    }
  });
}

export function useDeleteMiniLeague(uid: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (miniLeagueId: string) => deleteMiniLeague(miniLeagueId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['miniLeagues', uid] })
  });
}

export function useMarkNotificationsRead(uid: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (notificationIds?: string[]) => markNotificationsRead(notificationIds),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications', uid] })
  });
}
